/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import QuestionIcon from 'src/components/icons/QuestionIcon'
import BetaBadge from 'src/components/ts-components/BetaBadge'
import Text from 'src/components/ts-components/Text'

interface SectionHeaderProps {
  /** Already Title-cased by the caller (`capitalizeWords` / an i18n label helper). */
  title: string
  /** One short line explaining what the section is for. */
  subtitle?: string
  /** Support article opened in a new tab from the "?" icon. No icon without it. */
  helpUrl?: string
  /** The "?" icon's tooltip and accessible name. */
  helpLabel?: string
  /** Marks a section still being trialled with a BETA badge beside the title. */
  isBeta?: boolean
}

/**
 * A section's title row — title, optional BETA badge, optional "?" support
 * link — with a one-line subtitle beneath it. Same shape as the Team Finder
 * page header, at section scale.
 */
const SectionHeader = ({
  title,
  subtitle,
  helpUrl,
  helpLabel,
  isBeta = false,
}: SectionHeaderProps) => (
  <div css={wrapStyle}>
    <div css={titleRowStyle}>
      <Text variant='heading6' weight='semiBold' color={colors.NEUTRAL_0}>
        {title}
      </Text>
      {isBeta ? <BetaBadge /> : null}
      {helpUrl ? (
        <Tooltip title={helpLabel}>
          <a
            css={helpLinkStyle}
            href={helpUrl}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={helpLabel}
          >
            <QuestionIcon size={18} color={colors.PRIMARY} />
          </a>
        </Tooltip>
      ) : null}
    </div>
    {subtitle ? (
      <Text variant='paragraphSmall' weight='regular' color={colors.NEUTRAL_50}>
        {subtitle}
      </Text>
    ) : null}
  </div>
)

export default SectionHeader

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const titleRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const helpLinkStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  line-height: 0;

  &:hover {
    opacity: 0.85;
  }
`
